/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'
import { theme } from '../../styles/theme'
import { ReactNode } from 'react'

interface ButtonProps {
  children: ReactNode
  variant?: 'primary' | 'secondary' | 'outline' | 'subscribe' | 'payment' | 'telegram' | 'get-started'
  size?: 'sm' | 'md' | 'lg'
  disabled?: boolean
  onClick?: () => void
  type?: 'button' | 'submit' | 'reset'
  className?: string
  fullWidth?: boolean
  'data-bs-toggle'?: string
  'data-bs-target'?: string
  [key: string]: any
}

const getButtonStyles = (variant: string, fullWidth: boolean) => {
  const baseStyles = css`
    border: none;
    border-radius: ${theme.borderRadius.button};
    padding: 12px 30px;
    font-weight: 600;
    transition: ${theme.transitions.background};
    cursor: pointer;
    text-decoration: none;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    text-align: center;
    ${fullWidth && 'width: 100%;'}
    
    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
  `

  const variantStyles = {
    primary: css`
      background-color: ${theme.colors.primary};
      color: white;
      
      &:hover:not(:disabled) {
        background-color: ${theme.colors.secondary};
        color: white;
      }
    `,
    secondary: css`
      background-color: ${theme.colors.primary};
      color: white;
      width: 100%;
      
      &:hover:not(:disabled) {
        background-color: ${theme.colors.secondary};
        color: white;
      }
    `,
    outline: css`
      color: ${theme.colors.primary};
      border: 2px solid ${theme.colors.primary};
      background-color: transparent;
      
      &:hover:not(:disabled) {
        background-color: ${theme.colors.primary};
        color: white;
      }
    `,
    subscribe: css`
      background-color: ${theme.colors.primary};
      color: white;
      width: 100%;
      
      &:hover:not(:disabled) {
        background-color: ${theme.colors.secondary};
        color: white;
      }
    `,
    payment: css`
      background-color: ${theme.colors.primary};
      color: white;
      
      &:hover:not(:disabled) {
        background-color: ${theme.colors.secondary};
        color: white;
      }
    `,
    telegram: css`
      background-color: ${theme.colors.telegram};
      color: white;
      
      &:hover:not(:disabled) {
        background-color: ${theme.colors.telegramHover};
        color: white;
      }
    `,
    'get-started': css`
      background-color: #0275d8;
      color: white;
      
      &:hover:not(:disabled) {
        background-color: #025aa5;
        color: white;
      }
    `
  }

  return [baseStyles, variantStyles[variant as keyof typeof variantStyles]]
}

export const Button = ({ 
  children, 
  variant = 'primary', 
  disabled = false, 
  onClick, 
  type = 'button',
  className = '',
  fullWidth = false,
  ...props 
}: ButtonProps) => {
  const buttonStyles = getButtonStyles(variant, fullWidth)

  return (
    <button
      css={buttonStyles}
      type={type}
      disabled={disabled}
      onClick={onClick}
      className={className}
      {...props}
    >
      {children}
    </button>
  )
}